/**
 * useSpeurtocht Hook
 * Main hook for speurtocht game logic and session management
 */

import { useState, useCallback, useEffect, useMemo } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import {
  Speurtocht,
  SpeurttochtStep,
  SpeurttochtSession,
  SpeurttochtStepCompletion,
  SpeurttochtWithSteps,
  CompleteStepResult,
  StepSubmission,
  SessionStatus,
} from "../types";
import { 
  notifySpeurttochtStart, 
  notifySpeurttochtComplete, 
  notifySpeurttochtExpired,
  awardSpeurttochtPoints,
} from "../utils/notifications";

interface UseSpeurttochtReturn {
  /** Current speurtocht */
  speurtocht: SpeurttochtWithSteps | null;
  /** Current session (if started) */
  session: SpeurttochtSession | null;
  /** All step completions for current session */
  completions: SpeurttochtStepCompletion[];
  /** Current step (based on session progress) */
  currentStep: SpeurttochtStep | null;
  /** Current step index */
  currentStepIndex: number;
  /** Total steps */
  totalSteps: number;
  /** Completed steps count */
  completedSteps: number;
  /** Time remaining in seconds (if time limit) */
  timeRemaining: number | null;
  /** Whether session is active */
  isActive: boolean;
  /** Whether session is completed */
  isCompleted: boolean;
  /** Whether session is expired */
  isExpired: boolean;
  /** Loading state */
  isLoading: boolean;
  /** Submitting state */
  isSubmitting: boolean;
  /** Error message */
  error: string | null;
  /** Start the speurtocht */
  startSpeurtocht: () => Promise<string | null>;
  /** Complete current step */
  completeStep: (submission: StepSubmission) => Promise<CompleteStepResult | null>;
  /** Refetch speurtocht data */
  refetch: () => Promise<void>;
  /** Reset state */
  reset: () => void;
}

interface UseSpeurttochtOptions {
  /** Speurtocht ID to load */
  speurttochtId: string;
  /** Auto-fetch on mount */
  autoFetch?: boolean;
}

/**
 * Hook for managing a single speurtocht session
 */
export const useSpeurtocht = (
  options: UseSpeurttochtOptions
): UseSpeurttochtReturn => {
  const { speurttochtId, autoFetch = true } = options;
  const { activeProfile, userTeam, activeWeekend } = useAuth();

  // State
  const [speurtocht, setSpeurtocht] = useState<SpeurttochtWithSteps | null>(null);
  const [session, setSession] = useState<SpeurttochtSession | null>(null);
  const [completions, setCompletions] = useState<SpeurttochtStepCompletion[]>([]);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Current step based on session progress
   */
  const currentStep = useMemo(() => {
    if (!speurtocht?.steps || !session) return null;
    const activeSteps = speurtocht.steps.filter((s) => s.is_active);
    if (session.current_step_index >= activeSteps.length) return null;
    return activeSteps[session.current_step_index] || null;
  }, [speurtocht?.steps, session?.current_step_index]);

  const currentStepIndex = session?.current_step_index || 0;
  const totalSteps = session?.total_steps || speurtocht?.steps?.filter((s) => s.is_active).length || 0;
  const completedSteps = session?.completed_steps || 0;
  const isActive = session?.status === "active";
  const isCompleted = session?.status === "completed";
  const isExpired = session?.status === "expired";

  /**
   * Fetch speurtocht with steps
   */
  const fetchSpeurtocht = useCallback(async () => {
    if (!speurttochtId) return;

    setIsLoading(true);
    setError(null);

    try {
      // Fetch speurtocht
      const { data: speurttochtData, error: speurttochtError } = await supabase
        .from("speurtochts")
        .select(
          `
          *,
          creator:created_by(id, full_name, avatar_url)
        `
        )
        .eq("id", speurttochtId)
        .single();

      if (speurttochtError) {
        throw new Error(speurttochtError.message);
      }

      // Fetch steps
      const { data: stepsData, error: stepsError } = await supabase
        .from("speurtocht_steps")
        .select("*")
        .eq("speurtocht_id", speurttochtId)
        .eq("is_active", true)
        .order("step_order", { ascending: true });

      if (stepsError) {
        throw new Error(stepsError.message);
      }

      setSpeurtocht({
        ...(speurttochtData as Speurtocht),
        steps: stepsData as SpeurttochtStep[],
      });

      // Fetch session for user's team
      if (userTeam?.id) {
        const { data: sessionData, error: sessionError } = await supabase
          .from("speurtocht_sessions")
          .select(
            `
            *,
            started_by_user:started_by(id, full_name, avatar_url)
          `
          )
          .eq("speurtocht_id", speurttochtId)
          .eq("team_id", userTeam.id)
          .maybeSingle();

        if (sessionError) {
          console.error("Error fetching session:", sessionError);
        } else if (sessionData) {
          setSession(sessionData as SpeurttochtSession);

          // Calculate time remaining
          if (sessionData.expires_at) {
            const expiresAt = new Date(sessionData.expires_at);
            const now = new Date();
            const remaining = Math.max(
              0,
              Math.floor((expiresAt.getTime() - now.getTime()) / 1000)
            );
            setTimeRemaining(remaining);

            // Check if should be expired but hasn't been processed yet
            if (remaining === 0 && sessionData.status === "active") {
              // This session has expired but wasn't processed (user closed app, etc.)
              // We'll handle it after setting up local state
              console.log("Found unprocessed expired session, will handle...");
            }
          }

          // Fetch completions
          const { data: completionsData, error: completionsError } =
            await supabase
              .from("speurtocht_step_completions")
              .select(
                `
              *,
              step:step_id(*),
              completed_by_user:completed_by(id, full_name, avatar_url)
            `
              )
              .eq("session_id", sessionData.id)
              .order("completed_at", { ascending: true });

          if (completionsError) {
            console.error("Error fetching completions:", completionsError);
          } else {
            setCompletions(completionsData as SpeurttochtStepCompletion[]);
          }
        }
      }
    } catch (err) {
      console.error("Error fetching speurtocht:", err);
      setError(
        err instanceof Error ? err.message : "Fout bij laden speurtocht"
      );
    } finally {
      setIsLoading(false);
    }
  }, [speurttochtId, userTeam?.id]);

  /**
   * Start the speurtocht for the team
   */
  const startSpeurtocht = useCallback(async (): Promise<string | null> => {
    if (!speurttochtId || !userTeam?.id || !activeProfile?.id || !activeWeekend?.id) {
      setError("Je moet in een team zitten om te starten");
      return null;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Call the start_speurtocht function
      const { data, error: startError } = await supabase.rpc(
        "start_speurtocht",
        {
          p_speurtocht_id: speurttochtId,
          p_team_id: userTeam.id,
        }
      );

      if (startError) {
        throw new Error(startError.message);
      }

      // Send notification to all users that this team started
      if (speurtocht) {
        await notifySpeurttochtStart({
          speurttochtId,
          speurttochtName: speurtocht.name,
          teamId: userTeam.id,
          teamName: userTeam.name,
          weekendId: activeWeekend.id,
          startedByUserId: activeProfile.id,
          startedByUserName: activeProfile.full_name || "Gebruiker",
          config: speurtocht.notification_config,
        });
      }

      // Refetch to get the new session
      await fetchSpeurtocht();

      return data as string;
    } catch (err) {
      console.error("Error starting speurtocht:", err);
      setError(
        err instanceof Error ? err.message : "Fout bij starten speurtocht"
      );
      return null;
    } finally {
      setIsSubmitting(false);
    }
  }, [speurttochtId, userTeam?.id, userTeam?.name, activeProfile?.id, activeProfile?.full_name, activeWeekend?.id, speurtocht, fetchSpeurtocht]);

  /**
   * Complete the current step
   */
  const completeStep = useCallback(
    async (submission: StepSubmission): Promise<CompleteStepResult | null> => {
      if (!session?.id || !currentStep?.id || !userTeam?.id || !activeProfile?.id || !activeWeekend?.id) {
        setError("Geen actieve sessie of stap gevonden");
        return null;
      }

      setIsSubmitting(true);
      setError(null);

      try {
        // Call the complete_speurtocht_step function
        const { data, error: completeError } = await supabase.rpc(
          "complete_speurtocht_step",
          {
            p_session_id: session.id,
            p_step_id: currentStep.id,
            p_submission: submission,
          }
        );

        if (completeError) {
          throw new Error(completeError.message);
        }

        const result = data as CompleteStepResult;

        // Check if this was the final step (session is now completed)
        if (result.is_last_step && speurtocht) {
          // Calculate duration
          const startedAt = new Date(session.started_at);
          const completedAt = new Date();
          const durationSeconds = Math.floor((completedAt.getTime() - startedAt.getTime()) / 1000);

          // Send completion notification
          await notifySpeurttochtComplete({
            speurttochtId: speurtocht.id,
            speurttochtName: speurtocht.name,
            sessionId: session.id,
            teamId: userTeam.id,
            teamName: userTeam.name,
            weekendId: activeWeekend.id,
            totalPoints: result.total_points || 0,
            bonusPoints: result.bonus_points || 0,
            durationSeconds,
            isFirstTeam: result.is_first_team || false,
            config: speurtocht.notification_config,
          });
        }

        // Refetch to update local state
        await fetchSpeurtocht();

        return result;
      } catch (err) {
        console.error("Error completing step:", err);
        setError(
          err instanceof Error ? err.message : "Fout bij voltooien stap"
        );
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [session?.id, session?.started_at, currentStep?.id, speurtocht, userTeam?.id, userTeam?.name, activeProfile?.id, activeProfile?.full_name, activeWeekend?.id, fetchSpeurtocht]
  );

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    setSpeurtocht(null);
    setSession(null);
    setCompletions([]);
    setTimeRemaining(null);
    setError(null);
  }, []);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch && speurttochtId) {
      fetchSpeurtocht();
    }
  }, [autoFetch, speurttochtId, fetchSpeurtocht]);

  /**
   * Handle session expiration - award points and send notifications
   */
  const handleSessionExpiration = useCallback(async () => {
    if (!session?.id || !speurtocht || !userTeam?.id || !activeWeekend?.id) return;

    try {
      // Calculate duration
      const startedAt = new Date(session.started_at);
      const now = new Date();
      const durationSeconds = Math.floor((now.getTime() - startedAt.getTime()) / 1000);

      // Update session to expired with completed_at
      await supabase
        .from("speurtocht_sessions")
        .update({ 
          status: "expired" as SessionStatus,
          completed_at: now.toISOString(),
          duration_seconds: durationSeconds,
        })
        .eq("id", session.id);

      // Award partial points to team (if any steps were completed)
      if (session.total_points > 0 && activeProfile?.id) {
        await awardSpeurttochtPoints({
          teamId: userTeam.id,
          points: session.total_points,
          source: "completion",
          description: `Speurtocht "${speurtocht.name}" - ${session.completed_steps}/${session.total_steps} stappen (tijd verstreken)`,
          weekendId: activeWeekend.id,
          awardedByUserId: activeProfile.id,
          speurttochtId: speurtocht.id,
          sessionId: session.id,
        });
      }

      // Send notifications to all users
      await notifySpeurttochtExpired({
        speurttochtId: speurtocht.id,
        speurttochtName: speurtocht.name,
        sessionId: session.id,
        teamId: userTeam.id,
        teamName: userTeam.name,
        weekendId: activeWeekend.id,
        completedSteps: session.completed_steps,
        totalSteps: session.total_steps,
        pointsEarned: session.total_points,
        durationSeconds,
        config: speurtocht.notification_config,
      });

      // Update local state
      setSession((s) => s ? { 
        ...s, 
        status: "expired",
        completed_at: now.toISOString(),
        duration_seconds: durationSeconds,
      } : null);

    } catch (err) {
      console.error("Error handling session expiration:", err);
    }
  }, [session, speurtocht, userTeam, activeWeekend?.id, activeProfile?.id]);

  // Timer countdown
  useEffect(() => {
    if (!isActive || timeRemaining === null || timeRemaining <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev === null || prev <= 1) {
          // Time's up - handle expiration
          handleSessionExpiration();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isActive, timeRemaining, handleSessionExpiration]);

  // Realtime subscription for session updates
  useEffect(() => {
    if (!session?.id) return;

    const channel = supabase
      .channel(`speurtocht_session_${session.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "speurtocht_sessions",
          filter: `id=eq.${session.id}`,
        },
        (payload) => {
          if (payload.new) {
            setSession(payload.new as SpeurttochtSession);
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "speurtocht_step_completions",
          filter: `session_id=eq.${session.id}`,
        },
        async () => {
          // Refetch completions when a new one is added
          const { data } = await supabase
            .from("speurtocht_step_completions")
            .select(
              `
              *,
              step:step_id(*),
              completed_by_user:completed_by(id, full_name, avatar_url)
            `
            )
            .eq("session_id", session.id)
            .order("completed_at", { ascending: true });

          if (data) {
            setCompletions(data as SpeurttochtStepCompletion[]);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [session?.id]);

  // Check for unprocessed expired sessions after loading
  useEffect(() => {
    if (
      !isLoading && 
      session?.status === "active" && 
      session?.expires_at &&
      timeRemaining === 0
    ) {
      // This session should have expired but wasn't processed
      // Handle the expiration now
      handleSessionExpiration();
    }
  }, [isLoading, session?.status, session?.expires_at, timeRemaining, handleSessionExpiration]);

  return {
    speurtocht,
    session,
    completions,
    currentStep,
    currentStepIndex,
    totalSteps,
    completedSteps,
    timeRemaining,
    isActive,
    isCompleted,
    isExpired,
    isLoading,
    isSubmitting,
    error,
    startSpeurtocht,
    completeStep,
    refetch: fetchSpeurtocht,
    reset,
  };
};

